import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux';
import { FILE_UPLOAD_PURPOSE } from '@shafa/shared';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera } from '@fortawesome/free-solid-svg-icons';
import { postDoctorImageUpload } from './postDoctorImageUpload';
import { detailsFileDisplayUrl, hasDetailsFileSlot } from './detailsFiles';
import { Toast } from '../../utils/SwalUti';
import './user-profile.scss'

const UserInfo = ({ setInfoFun, info, handleSubmit, isSubmitting, setDetailsFileField }) => {
    const { user, selectedRole } = useSelector((state) => state.auth);
    const [profilePicUploading, setProfilePicUploading] = useState(false);
    const [profilePicPreviewUrl, setProfilePicPreviewUrl] = useState(null);

    const displayName = info?.name || user?.user?.name || '';
    const initials = useMemo(() => {
        const parts = (displayName || '').trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return '?';
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }, [displayName]);

    const onSubmit = (event) => {
        event.preventDefault()
        handleSubmit()
    }

    const [savedProfilePicUrl, setSavedProfilePicUrl] = useState('');
    useEffect(() => {
        let active = true;
        (async () => {
            const url = await detailsFileDisplayUrl(info?.details?.files?.profilePicture, { concatPublic: true });
            if (active) setSavedProfilePicUrl(url || '');
        })();
        return () => { active = false; };
    }, [info?.details?.files?.profilePicture]);

    const handleProfilePictureChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setProfilePicUploading(true);
        try {
            const orgId = selectedRole?.organization?._id || selectedRole?.organization;
            const res = await postDoctorImageUpload(file, {
                purpose: FILE_UPLOAD_PURPOSE.DOCTOR_PROFILE_PICTURE,
                organizationId: orgId,
            });
            if (res.status === 201 && res.data?.file?._id) {
                setDetailsFileField('profilePicture', res.data.file);
                if (res.data.url) {
                    setProfilePicPreviewUrl(res.data.url);
                }
                Toast.fire({
                    icon: 'success',
                    title: 'Photo ready — click Save to keep it on your profile',
                });
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Upload failed';
            Toast.fire({ icon: 'error', title: msg });
        } finally {
            setProfilePicUploading(false);
            e.target.value = '';
        }
    };

    const hasSavedPhoto = !profilePicPreviewUrl && hasDetailsFileSlot(info?.details?.files, 'profilePicture');

    return (
        <div className='basic-info profile-panel'>

            <form onSubmit={onSubmit}>
                <div className="profile-panel__toolbar">
                    <div>
                        <h2 className="profile-panel__title">Basic information</h2>
                        <p className="profile-panel__subtitle">Name, contact, and how you appear in the app.</p>
                    </div>
                    <button
                        type="submit"
                        className="btn save-button"
                    >
                        Save changes
                        {isSubmitting && (
                            <span
                                className="spinner-border spinner-border-sm ml-2"
                                role="status"
                                aria-hidden="true"
                            ></span>
                        )}
                    </button>
                </div>

                <div className="profile-hero">
                    <div className="profile-avatar-wrap">
                        <div className="profile-avatar-ring">
                            {profilePicPreviewUrl || savedProfilePicUrl ? (
                                <img
                                    alt=""
                                    src={savedProfilePicUrl || profilePicPreviewUrl}
                                    className="profile-avatar"
                                />
                            ) : (
                                <div className="profile-avatar profile-avatar--placeholder" aria-hidden>
                                    {initials}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="profile-hero__body">
                        <div className="profile-hero__label">Profile photo</div>
                        <p className="profile-hero__hint mb-0">
                            {hasSavedPhoto
                                ? 'A photo is saved on your profile. Upload a new image to replace it.'
                                : 'JPG, PNG or Webp. Shown where your name appears to colleagues.'}
                        </p>
                        <label className="profile-btn-upload mb-0">
                            <FontAwesomeIcon icon={faCamera} size="xs" />
                            {profilePicUploading ? 'Uploading…' : 'Upload photo'}
                            <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                className="d-none"
                                onChange={handleProfilePictureChange}
                                disabled={profilePicUploading}
                            />
                        </label>
                    </div>
                </div>

                <div className="profile-fields">
                    <div className="profile-field profile-field--row">
                        <label htmlFor="profile-name" className="profile-field__label form-label">
                            Full name<span className="text-danger">*</span>
                        </label>
                        <div className="profile-field__control">
                            <input
                                required
                                id="profile-name"
                                className="form-control input"
                                autoComplete="name"
                                type="text"
                                name="name"
                                value={info?.name || ""}
                                placeholder="Your name"
                                onChange={(e) => setInfoFun("name", e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="profile-field profile-field--row">
                        <label htmlFor="profile-email" className="profile-field__label form-label">Email</label>
                        <div className="profile-field__control">
                            <input
                                id="profile-email"
                                className="form-control input"
                                autoComplete="email"
                                type="email"
                                name="email"
                                placeholder="you@example.com"
                                value={info?.email || ""}
                                onChange={(e) => setInfoFun("email", e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="profile-field profile-field--row">
                        <label htmlFor="profile-phone" className="profile-field__label form-label">Phone</label>
                        <div className="profile-field__control">
                            <input
                                id="profile-phone"
                                className="form-control input"
                                autoComplete="tel"
                                pattern="(^(01){1}[3-9]{1}\d{8})$"
                                type="text"
                                name="phone"
                                placeholder="01XXXXXXXXX"
                                value={info?.phone || ""}
                                onChange={(e) => setInfoFun("phone", e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="profile-field profile-field--row">
                        <label htmlFor="profile-dob" className="profile-field__label form-label">
                            Date of birth<span className="text-danger">*</span>
                        </label>
                        <div className="profile-field__control">
                            <input
                                required
                                id="profile-dob"
                                className="form-control input"
                                autoComplete="bday"
                                type="date"
                                name="dob"
                                value={info?.dob?.slice(0, 10) || ""}
                                onChange={(e) => setInfoFun("dob", e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="profile-field profile-field--row">
                        <span className="profile-field__label form-label mb-0">
                            Gender<span className="text-danger">*</span>
                        </span>
                        <div className="profile-field__control">
                            <div className="profile-gender" role="group" aria-label="Gender">
                                <label className={`profile-gender__opt ${info?.gender?.toLowerCase() === "male" ? "is-selected" : ""}`}>
                                    <input
                                        checked={info?.gender?.toLowerCase() === "male"}
                                        type="radio"
                                        name="gender"
                                        value="Male"
                                        onChange={() => setInfoFun("gender", "Male")}
                                    />
                                    Male
                                </label>
                                <label className={`profile-gender__opt ${info?.gender?.toLowerCase() === "female" ? "is-selected" : ""}`}>
                                    <input
                                        checked={info?.gender?.toLowerCase() === "female"}
                                        type="radio"
                                        name="gender"
                                        value="Female"
                                        onChange={() => setInfoFun("gender", "Female")}
                                    />
                                    Female
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="profile-field">
                        <label htmlFor="profile-address" className="profile-field__label form-label">Address</label>
                        <textarea
                            id="profile-address"
                            autoComplete="street-address"
                            placeholder="Address"
                            value={info?.address || ""}
                            onChange={(e) => setInfoFun("address", e.target.value)}
                            rows={3}
                        />
                    </div>
                </div>
            </form>
        </div>
    )
}

export default UserInfo
